/**
 * ContextBar
 * Global filter bar (Account Manager, Project Manager) rendered below the nav
 */

import React, { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { X, ChevronDown, Filter, Database, Search } from 'lucide-react'
import { useGlobalFilters } from '../contexts/GlobalFilterContext'
import { useDateRange } from '../contexts/DateRangeContext'
import { API_BASE_URL, authHeaders } from '../config/env'
import DateRangeSelector from './DateRangeSelector'

function FilterDropdown({ label, value, options, onChange, isLoading, searchable = false }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef(null)
  const searchRef = useRef(null)

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    if (open && searchable) {
      setSearch('')
      setTimeout(() => searchRef.current?.focus(), 0)
    }
  }, [open, searchable])

  const selectedName = value
    ? options?.find((o) => o.id === value)?.name || 'Unknown'
    : null

  const filtered = searchable && search
    ? options?.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()))
    : options

  return (
    <div className="relative" ref={ref}>
      {selectedName ? (
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium
            bg-blue-100 text-blue-700 border border-blue-300
            dark:bg-blue-600/15 dark:text-blue-400 dark:border-blue-500/30
            hover:bg-blue-200 dark:hover:bg-blue-600/25 transition-colors"
        >
          <span className="text-blue-500 dark:text-blue-500/70 text-[10px] uppercase tracking-wider">{label}:</span>
          {selectedName}
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange(null) }}
            className="ml-0.5 hover:text-blue-900 dark:hover:text-blue-300 transition-colors cursor-pointer"
          >
            <X size={12} />
          </span>
        </button>
      ) : (
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs
            text-slate-600 hover:text-slate-800 hover:bg-slate-100
            dark:text-slate-400 dark:hover:text-slate-300 dark:hover:bg-slate-800
            border border-slate-300 dark:border-slate-700/50 transition-colors"
        >
          {label}
          <ChevronDown size={12} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
        </button>
      )}

      {open && (
        <div className="absolute top-full right-0 mt-1 z-50 w-64
          bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden">
          {searchable && (
            <div className="px-2 py-1.5 border-b border-slate-200 dark:border-slate-700/50">
              <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-50 dark:bg-slate-700/50">
                <Search size={11} className="text-slate-400 flex-shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Search ${label.toLowerCase()}s...`}
                  className="w-full bg-transparent text-xs text-slate-700 dark:text-slate-300 outline-none placeholder:text-slate-400"
                />
              </div>
            </div>
          )}
          <div
            onClick={() => { onChange(null); setOpen(false) }}
            className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer
              border-b border-slate-200 dark:border-slate-700/50"
          >
            All {label}s
          </div>
          <div className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-2 text-xs text-slate-500">Loading...</div>
            ) : filtered?.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">No matches</div>
            ) : (
              filtered?.map((opt) => (
                <div
                  key={opt.id}
                  onClick={() => { onChange(opt.id); setOpen(false) }}
                  className={`px-3 py-2 text-xs cursor-pointer transition-colors
                    ${opt.id === value
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-600/20 dark:text-blue-400'
                      : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                    }`}
                >
                  {opt.name}
                  <span className="ml-1.5 text-slate-400 dark:text-slate-500">({opt.project_count})</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function ContextBar() {
  const {
    filters,
    setAccountManager,
    setProjectManager,
    setSalesCode,
    clearAll,
    hasActiveFilters,
  } = useGlobalFilters()
  const { getApiDateRange } = useDateRange()
  const dateRange = getApiDateRange()
  const { pathname } = useLocation()
  const countEntity = pathname.startsWith('/jobs') ? 'jobs' : 'projects'

  // Build query params for cascading filters
  const buildParams = (extra = {}) => {
    const params = new URLSearchParams()
    if (dateRange.start_date) params.set('date_from', dateRange.start_date)
    if (dateRange.end_date) params.set('date_to', dateRange.end_date)
    if (countEntity !== 'projects') params.set('count_entity', countEntity)
    Object.entries(extra).forEach(([k, v]) => { if (v) params.set(k, v) })
    const qs = params.toString()
    return qs ? `?${qs}` : ''
  }

  // AM list — cascades from date range + PM + sales code
  const { data: accountManagers, isLoading: amLoading } = useQuery({
    queryKey: ['globalFilter-accountManagers', dateRange, filters.projectManagerId, filters.salesCodeId, countEntity],
    queryFn: async () => {
      const res = await fetch(
        `${API_BASE_URL}/filters/account-managers${buildParams({
          project_manager_id: filters.projectManagerId,
          sales_code_id: filters.salesCodeId,
        })}`,
        { headers: authHeaders() }
      )
      if (!res.ok) return []
      return res.json()
    },
    staleTime: 1000 * 60 * 5,
  })

  // PM list — cascades from date range + AM + sales code
  const { data: projectManagers, isLoading: pmLoading } = useQuery({
    queryKey: ['globalFilter-projectManagers', dateRange, filters.accountManagerId, filters.salesCodeId, countEntity],
    queryFn: async () => {
      const res = await fetch(
        `${API_BASE_URL}/filters/project-managers${buildParams({
          account_manager_id: filters.accountManagerId,
          sales_code_id: filters.salesCodeId,
        })}`,
        { headers: authHeaders() }
      )
      if (!res.ok) return []
      return res.json()
    },
    staleTime: 1000 * 60 * 5,
  })

  // Sales code list — cascades from date range + AM + PM
  const { data: salesCodes, isLoading: scLoading } = useQuery({
    queryKey: ['globalFilter-salesCodes', dateRange, filters.accountManagerId, filters.projectManagerId, countEntity],
    queryFn: async () => {
      const res = await fetch(
        `${API_BASE_URL}/filters/sales-codes${buildParams({
          account_manager_id: filters.accountManagerId,
          project_manager_id: filters.projectManagerId,
        })}`,
        { headers: authHeaders() }
      )
      if (!res.ok) return []
      const data = await res.json()
      return data.map(c => ({ id: c.sales_code_id, name: c.sales_code_name, project_count: c.project_count }))
    },
    staleTime: 1000 * 60 * 5,
  })

  // Auto-clear selection if it's no longer in the narrowed list
  useEffect(() => {
    if (filters.accountManagerId && accountManagers && !accountManagers.find(o => o.id === filters.accountManagerId)) {
      setAccountManager(null)
    }
  }, [accountManagers, filters.accountManagerId, setAccountManager])

  useEffect(() => {
    if (filters.projectManagerId && projectManagers && !projectManagers.find(o => o.id === filters.projectManagerId)) {
      setProjectManager(null)
    }
  }, [projectManagers, filters.projectManagerId, setProjectManager])

  useEffect(() => {
    if (filters.salesCodeId && salesCodes && !salesCodes.find(o => o.id === filters.salesCodeId)) {
      setSalesCode(null)
    }
  }, [salesCodes, filters.salesCodeId, setSalesCode])

  const { data: freshness } = useQuery({
    queryKey: ['data-freshness'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/data-freshness`, { headers: authHeaders() })
      if (!res.ok) return null
      return res.json()
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })

  const refreshDate = freshness?.last_refresh
    ? new Date(freshness.last_refresh).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : null

  return (
    <div className="bg-slate-100 border-b border-slate-200 dark:bg-slate-900/60 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-between">
        {/* Left: data snapshot */}
        <div className="flex items-center gap-1.5">
          {refreshDate && (
            <>
              <Database size={11} className="text-slate-400 dark:text-slate-500" />
              <span className="text-[11px] text-slate-400 dark:text-slate-500 tracking-wide uppercase">
                Data snapshot {refreshDate}
              </span>
            </>
          )}
        </div>

        {/* Right: date range + global filters */}
        <div className="flex items-center gap-3">
          <DateRangeSelector />
          <div className="w-px h-5 bg-slate-300 dark:bg-slate-700/50" />
          <Filter size={13} className="text-slate-400 dark:text-slate-500 flex-shrink-0" />

          <FilterDropdown
            label="Account Manager"
            value={filters.accountManagerId}
            options={accountManagers}
            onChange={setAccountManager}
            isLoading={amLoading}
          />

          <FilterDropdown
            label="Project Manager"
            value={filters.projectManagerId}
            options={projectManagers}
            onChange={setProjectManager}
            isLoading={pmLoading}
          />

          <FilterDropdown
            label="Sales Code"
            value={filters.salesCodeId}
            options={salesCodes}
            onChange={setSalesCode}
            isLoading={scLoading}
            searchable
          />

          {hasActiveFilters && (
            <button
              onClick={clearAll}
              className="text-[10px] uppercase tracking-wider text-slate-500
                hover:text-slate-700 dark:hover:text-slate-300 transition-colors ml-1"
            >
              Clear all
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
